import { useEffect, useState } from "react";
import axios from "axios";
import { MessageCircle, Phone, Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useLang, WHATSAPP_URL, PHONE_TEL, PHONE_LABEL } from "@/lib/i18n";
import { Reveal, Eyebrow } from "./Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Booking() {
  const { t, lang } = useLang();
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    check_in: "",
    check_out: "",
    guests: "2",
    message: "",
  });
  const [sending, setSending] = useState(false);
  const [paypalOk, setPaypalOk] = useState(false);
  const [razorOk, setRazorOk] = useState(false);
  const [paystackOk, setPaystackOk] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/payments/methods`)
      .then(({ data }) => {
        setPaypalOk(!!data.paypal);
        setRazorOk(!!data.razorpay);
        setPaystackOk(!!data.paystack);
      })
      .catch(() => {
        setPaypalOk(false);
        setRazorOk(false);
        setPaystackOk(false);
      });
  }, []);

  const payPaystack = async () => {
    try {
      const { data } = await axios.post(`${API}/payments/paystack/create`, { week: "", origin_url: window.location.origin, email: form.email });
      window.location.href = data.approval_url;
    } catch (err) {
      toast.error(t.booking.toastErr);
    }
  };

  const payRazorpay = async () => {
    try {
      const { data } = await axios.post(`${API}/payments/razorpay/create`, { week: "", origin_url: window.location.origin });
      window.location.href = data.approval_url;
    } catch (err) {
      toast.error(t.booking.toastErr);
    }
  };

  const payPaypal = async () => {
    try {
      const { data } = await axios.post(`${API}/payments/paypal/create`, { week: "", origin_url: window.location.origin });
      window.location.href = data.approval_url;
    } catch (err) {
      toast.error(t.booking.toastErr);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success(t.booking.toastOk);
      setForm({ name: "", email: "", phone: "", check_in: "", check_out: "", guests: "2", message: "" });
    } catch (err) {
      toast.error(t.booking.toastErr);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "h-11 w-full rounded-md border border-line bg-sand px-3.5 text-sm text-ink outline-none transition-colors duration-300 placeholder:text-ink2/60 focus:border-olive";

  return (
    <section id="contatti" className="bg-ink py-24 text-sand md:py-36">
      <div className="mx-auto grid max-w-7xl gap-14 px-5 md:grid-cols-2 md:gap-20 md:px-10">
        <div>
          <Reveal>
            <Eyebrow light>{t.booking.eyebrow}</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-5 font-serif text-4xl font-light leading-[1.05] md:text-6xl">
              {t.booking.title.split(" ")[0]}{" "}
              <span className="italic text-terra">
                {t.booking.title.split(" ").slice(1).join(" ")}
              </span>
            </h2>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-md text-base leading-relaxed text-sand/75 md:text-lg">
              {t.booking.p}
            </p>
          </Reveal>

          <Reveal delay={0.24} className="mt-10 flex flex-col gap-4">
            <a
              href="https://www.booking.com/hotel/it/private-oasis-villa1.it.html"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-booking-btn"
              className="group flex w-fit items-center gap-3 rounded-full bg-[#003580] px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:shadow-xl hover:shadow-[#003580]/30"
            >
              Prenota su Booking.com
            </a>
            <a
              href="https://www.airbnb.it/rooms/26104436"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-airbnb-btn"
              className="flex w-fit items-center gap-3 rounded-full bg-[#FF385C] px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:shadow-xl hover:shadow-[#FF385C]/25"
            >
              Prenota su Airbnb
            </a>
            <a
              href={`${WHATSAPP_URL}?text=${encodeURIComponent(t.booking.whatsappMsg)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-whatsapp-btn"
              className="group flex w-fit items-center gap-3 rounded-full bg-[#1fa855] px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-white transition-all duration-300 hover:shadow-xl hover:shadow-[#1fa855]/25"
            >
              <MessageCircle className="h-5 w-5 transition-transform duration-300 group-hover:scale-110" />
              {t.booking.whatsapp}
            </a>
            <a
              href="mailto:info@privateoasisvilla.com?subject=Richiesta%20disponibilit%C3%A0%20-%20Private%20Oasis%20Villa"
              data-testid="booking-email-btn"
              className="group flex w-fit items-center gap-3 rounded-full border border-sand/30 px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-sand transition-all duration-300 hover:border-sand hover:bg-sand hover:text-ink"
            >
              <Mail className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
              {lang === "it" ? "Scrivici via email" : "Email us"}
            </a>
            <a
              href={PHONE_TEL}
              data-testid="booking-phone-btn"
              className="group flex w-fit items-center gap-3 rounded-full border border-sand/30 px-7 py-4 text-sm font-semibold uppercase tracking-[0.14em] text-sand transition-all duration-300 hover:border-sand hover:bg-sand hover:text-ink"
            >
              <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
              {t.booking.call} · {PHONE_LABEL}
            </a>
            {paypalOk && (
              <button
                onClick={payPaypal}
                data-testid="booking-paypal-btn"
                className="flex w-fit items-center gap-2.5 rounded-full bg-[#FFD140] px-7 py-4 text-sm font-bold italic tracking-wide text-[#003087] transition-all duration-300 hover:shadow-xl hover:shadow-[#FFD140]/25"
              >
                Paga acconto con <span className="not-italic font-extrabold text-[#0070BA]">PayPal</span>
              </button>
            )}
            {razorOk && (
              <button
                onClick={payRazorpay}
                data-testid="booking-razorpay-btn"
                className="flex w-fit items-center gap-2.5 rounded-full bg-[#0C2451] px-7 py-4 text-sm font-bold text-white transition-all duration-300 hover:shadow-xl hover:shadow-[#0C2451]/30"
              >
                Paga acconto con <span className="text-[#8FC74A]">Razorpay</span>
              </button>
            )}
            {paystackOk && (
              <button
                onClick={payPaystack}
                data-testid="booking-paystack-btn"
                className="flex w-fit items-center gap-2.5 rounded-full bg-[#09D26E] px-7 py-4 text-sm font-bold text-[#011B33] transition-all duration-300 hover:shadow-xl hover:shadow-[#09D26E]/25"
              >
                Paga acconto con Paystack
              </button>
            )}
            <a
              href="mailto:info@privateoasisvilla.com"
              data-testid="booking-email-address"
              className="flex w-fit items-center gap-2 text-sm text-sand/70 transition-colors duration-300 hover:text-terra"
            >
              <Mail className="h-3.5 w-3.5" />
              info@privateoasisvilla.com
            </a>
            <p className="mt-4 text-sm text-sand/60">{t.location.address}</p>
          </Reveal>
        </div>

        <Reveal delay={0.15} y={40}>
          <form
            onSubmit={submit}
            data-testid="booking-form"
            className="rounded-lg border border-sand/10 bg-sand p-7 text-ink md:p-10"
          >
            <div className="grid gap-5">
              <div className="grid gap-2">
                <Label htmlFor="bk-name">{t.booking.formName}</Label>
                <Input
                  id="bk-name"
                  data-testid="booking-name-input"
                  required
                  value={form.name}
                  onChange={set("name")}
                  className="h-11 rounded-md border-line bg-white focus-visible:ring-olive"
                  placeholder="Mario Rossi"
                />
              </div>
              <div className="grid gap-5 sm:grid-cols-2">
                <div className="grid gap-2">
                  <Label htmlFor="bk-email">{t.booking.formEmail}</Label>
                  <Input
                    id="bk-email"
                    data-testid="booking-email-input"
                    type="email"
                    required
                    value={form.email}
                    onChange={set("email")}
                    className="h-11 rounded-md border-line bg-white focus-visible:ring-olive"
                    placeholder="mario@email.it"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bk-phone">{t.booking.formPhone}</Label>
                  <Input
                    id="bk-phone"
                    data-testid="booking-phone-input"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    className="h-11 rounded-md border-line bg-white focus-visible:ring-olive"
                    placeholder="+39 …"
                  />
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-3">
                <div className="grid gap-2">
                  <Label htmlFor="bk-in">{t.booking.formCheckin}</Label>
                  <Input
                    id="bk-in"
                    data-testid="booking-checkin-input"
                    type="date"
                    value={form.check_in}
                    onChange={set("check_in")}
                    className="h-11 rounded-md border-line bg-white focus-visible:ring-olive"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bk-out">{t.booking.formCheckout}</Label>
                  <Input
                    id="bk-out"
                    data-testid="booking-checkout-input"
                    type="date"
                    value={form.check_out}
                    onChange={set("check_out")}
                    className="h-11 rounded-md border-line bg-white focus-visible:ring-olive"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="bk-guests">{t.booking.formGuests}</Label>
                  <select
                    id="bk-guests"
                    data-testid="booking-guests-select"
                    value={form.guests}
                    onChange={set("guests")}
                    className={`${inputCls} bg-white`}
                  >
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <option key={n} value={n}>
                        {n} {t.booking.guestUnit}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="bk-msg">{t.booking.formMessage}</Label>
                <Textarea
                  id="bk-msg"
                  data-testid="booking-message-input"
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  className="resize-none rounded-md border-line bg-white focus-visible:ring-olive"
                />
              </div>
              <button
                type="submit"
                data-testid="booking-form-submit"
                disabled={sending}
                className="group relative mt-1 flex h-12 items-center justify-center gap-2.5 overflow-hidden rounded-full bg-terra text-sm font-semibold uppercase tracking-[0.16em] text-sand transition-all duration-300 hover:bg-olive disabled:opacity-70"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.booking.formSending}
                  </>
                ) : (
                  <>
                    {t.booking.formSubmit}
                    <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                  </>
                )}
              </button>
              <p
                data-testid="form-email-note"
                className="mt-1 flex items-center justify-center gap-2 text-xs text-ink2"
              >
                <Mail className="h-3.5 w-3.5 text-terra" />
                {lang === "it" ? "Preferisci l'email? " : "Prefer email? "}
                <a
                  href="mailto:info@privateoasisvilla.com"
                  className="font-semibold text-olive underline decoration-terra/50 underline-offset-2 transition-colors duration-300 hover:text-terra"
                >
                  info@privateoasisvilla.com
                </a>
              </p>
            </div>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
